import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { minutesToHHMM } from './duration'

/**
 * Merge Tailwind CSS classes with clsx
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Duracao em MINUTOS -> `HH:MM` (spec r4-01: um formato de tempo so, em todo o sistema).
 *   90  -> "01:30"
 *   45  -> "00:45"
 *   600 -> "10:00"
 *   0   -> "00:00"
 *
 * Era "1h30min". A assinatura ficou para os dezoito chamadores nao mudarem; a regra
 * mora em `@/lib/duration`.
 */
export function formatDuration(minutes: number): string {
  return minutesToHHMM(minutes)
}

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/

/**
 * Data pura 'AAAA-MM-DD' -> Date na meia-noite LOCAL. `new Date('2026-10-19')` le a
 * string como UTC, e no fuso de Sao Paulo isso vira 18/10 21:00 (MH13-32). Qualquer
 * outra string (timestamp com hora/fuso) segue o parser nativo.
 */
export function parseDateOnly(value: string): Date {
  const match = DATE_ONLY.exec(value)
  if (!match) return new Date(value)
  const [, year, month, day] = match
  return new Date(Number(year), Number(month) - 1, Number(day))
}

/**
 * Format date to Brazilian locale
 */
export function formatDate(date: Date | string, options?: Intl.DateTimeFormatOptions): string {
  const d = typeof date === 'string' ? parseDateOnly(date) : date
  return d.toLocaleDateString('pt-BR', options)
}

/**
 * Format date with time
 */
export function formatDateTime(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date
  return d.toLocaleString('pt-BR')
}

/**
 * Format relative time (e.g., "2 dias atrás")
 */
export function formatRelativeTime(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date
  const now = new Date()
  const diffMs = now.getTime() - d.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays === 0) return 'Hoje'
  if (diffDays === 1) return 'Ontem'
  if (diffDays < 7) return `${diffDays} dias atrás`
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} semanas atrás`
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} meses atrás`
  return `${Math.floor(diffDays / 365)} anos atrás`
}

/**
 * Delay utility for async operations
 */
export function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

/**
 * Truncate text with ellipsis
 */
export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text
  return text.slice(0, maxLength - 3) + '...'
}
